import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KeyboardArrowDown, MoreHoriz, PushPinOutlined } from '@mui/icons-material';
import { Checkbox, IconButton, Menu, MenuItem, Popper, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { isEmpty, isNil, sortBy } from 'lodash';
import { AI_GRADIENT_CSS, aiGradientTextSx } from 'common/constants/ai';
import AddMultipleMenuItem from 'components/FeaturedSpreadsheet/components/AddMultipleMenuItem';
import { useAIFieldAcceptContext } from 'pages/CapTable/cap-table/components/captable-table/AIFieldAcceptContext';
import { isPendingMeta } from 'pages/CapTable/cap-table/utilities/securityAiPatches';
import { SORT_DESC } from 'pages/Valuations/util/constants';
import { useResponse } from 'services/hooks';
import theme from 'theme';
import { getStringValue } from 'utilities';
import { getColumnIndex, getColumnLetter } from 'utilities/alphabet-utilities';
import { SortAscIcon, SortDescIcon } from './SortIcons';
import { ReverseParsedFundOwnershipDetail } from '../../../pages/CapTable/fund-ownership/types';
import FeaturedSpreadsheetContext from '../context/FeaturedSpreadsheetContext';
import { copyToClipboard, getCellRef, getValuesToCopy, hasCellColSpan } from '../utilities/utilities';

interface StyledColumnIndicatorProps {
  allowReorderColumns?: boolean;
}

interface StyledMenuButtonProps {
  cursorType?: React.CSSProperties['cursor'];
  emphasizeViewerFreezeAction?: boolean;
}

const shouldForwardColumnIndicatorProp = (prop: PropertyKey): boolean => prop !== 'allowReorderColumns';

const shouldForwardMenuButtonProp = (prop: PropertyKey): boolean =>
  prop !== 'cursorType' && prop !== 'emphasizeViewerFreezeAction';

const iconStyles = {
  fill: `${theme.palette.white} !important`,
  width: 'auto !important',
  height: '15px !important',
};

const StyledColumnIndicator = styled('div', {
  shouldForwardProp: shouldForwardColumnIndicatorProp,
})<StyledColumnIndicatorProps>(({ allowReorderColumns }) => ({
  padding: 0,
  position: 'absolute',
  top: '0',
  backgroundColor: theme.palette.primary[500],
  borderRadius: '10px 10px 0px 0px',
  height: '12px',
  textAlign: 'center',
  cursor: allowReorderColumns ? 'move' : 'default',
}));

const StyledMenuButton = styled(IconButton, {
  shouldForwardProp: shouldForwardMenuButtonProp,
})<StyledMenuButtonProps>(({ cursorType, emphasizeViewerFreezeAction }) => ({
  maxWidth: emphasizeViewerFreezeAction ? '18px' : '12px',
  minWidth: emphasizeViewerFreezeAction ? '18px' : '12px',
  color: theme.palette.white,
  padding: '0',
  marginLeft: emphasizeViewerFreezeAction ? '-3px' : 0,
  marginTop: emphasizeViewerFreezeAction ? '-17px' : '-14px',
  height: emphasizeViewerFreezeAction ? '18px' : '12px',
  cursor: cursorType || 'default',
  '& > svg': emphasizeViewerFreezeAction
    ? {
      fontSize: '18px !important',
      height: '18px !important',
    }
    : {},
}));

const StyledMoreHorizIcon = styled(MoreHoriz)(() => iconStyles);

const StyledKeyboardArrowDownIcon = styled(KeyboardArrowDown)(() => iconStyles);

const StyledDeleteMenuItem = styled(MenuItem)(() => ({
  color: theme.palette.pink,
}));

const StyledAcceptAllMenuItem = styled(MenuItem)(() => ({
  fontWeight: 600,
  '&:hover': {
    background: AI_GRADIENT_CSS,
    '& .MuiTypography-root': {
      background: 'none',
      WebkitBackgroundClip: 'unset',
      WebkitTextFillColor: 'white',
    },
  },
}));

interface StyledMenuProps {
  compactViewerOptions: boolean;
}

const StyledMenu = styled(Menu, {
  shouldForwardProp: prop => prop !== 'compactViewerOptions',
})<StyledMenuProps>(({ compactViewerOptions }) =>
  compactViewerOptions
    ? {
      '& .MuiPaper-root': {
        minWidth: '188px',
        maxWidth: '220px',
      },
      '& .MuiList-root': {
        padding: '4px 0',
      },
      '& .MuiMenuItem-root': {
        minHeight: '28px',
        padding: '3px 8px 3px 10px',
        fontSize: '12px',
        lineHeight: 1.25,
        gap: '8px',
        whiteSpace: 'nowrap',
      },
      '& .MuiMenuItem-root > .MuiSvgIcon-root': {
        flex: '0 0 auto',
        fontSize: '16px',
      },
      '& .MuiCheckbox-root': {
        marginLeft: 'auto',
        padding: '2px',
      },
      '& .MuiCheckbox-root .MuiSvgIcon-root': {
        fontSize: '16px',
      },
    }
    : {}
);

interface ColumnIndicatorProps {
  frozenPaneCellKey?: string;
  hiddenColumns?: {
    right?: number;
    left?: number;
  };
  onToggleFrozenPaneAtCell?: (cellKey: string) => void;
  tableRef: React.RefObject<HTMLElement> | null;
}

interface IndicatorPosition {
  left: number;
  width: number;
}

interface CellInfo {
  key?: string;
  columnLegend?: string;
  y?: number;
  columnId?: number | string;
}

interface Column {
  id?: number | string;
  order?: number;
  isLTM?: boolean;
  isNTM?: boolean;
  isDeleteableColumn?: boolean;
  isSortableColumn?: boolean;
  isStringColumn?: boolean;
  isDateColumn?: boolean;
  fields?: Array<Record<string, string>>;
  parentColumn?: any;
}

const ColumnIndicator: React.FC<ColumnIndicatorProps> = ({
  frozenPaneCellKey,
  tableRef,
  hiddenColumns,
  onToggleFrozenPaneAtCell,
}) => {
  const [showIndicator, setShowIndicator] = useState<boolean>(false);
  const [indicatorPosition, setIndicatorPosition] = useState<IndicatorPosition | null>(null);
  const [isIndicatorActionable, setIsIndicatorActionable] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [cell, setCell] = useState<CellInfo | null>(null);
  const [multipleValue, setMultipleValue] = useState<number>(0);
  const {
    addMultipleColumns,
    allowAddMultipleColumns,
    allowCloneColumn,
    allowConfirmAndDeleteColumn,
    allowCopyColumn,
    allowDeleteColumn,
    allowDeleteOnlySpecificColumn,
    allowMergedSelectionIndicatorSpan,
    allowReorderColumns,
    allowSortColumn,
    cells,
    cloneColumn,
    columns,
    displayColumnIndicator,
    format,
    handleDeleteColumn,
    selectedCell,
    setClearSelected,
    setSelectedCell,
    sortColumnFn,
    sortedColumn,
    workbook,
  } = useContext(FeaturedSpreadsheetContext);

  const { acceptAllFields, dismissAllFields } = useAIFieldAcceptContext();

  const { infoNotification, errorNotification } = useResponse();
  const hasConfiguredColumnActions = Boolean(
    (allowAddMultipleColumns && addMultipleColumns) ||
      (allowCloneColumn && cloneColumn) ||
      allowCopyColumn ||
      allowDeleteColumn ||
      allowDeleteOnlySpecificColumn ||
      allowReorderColumns ||
      allowSortColumn ||
      onToggleFrozenPaneAtCell
  );

  useEffect(() => {
    if (!selectedCell || !displayColumnIndicator) {
      setIndicatorPosition(null);
      setIsIndicatorActionable(false);
      setShowIndicator(false);
      return;
    }
    const { start, end, cellInfo } = selectedCell;

    setCell(cellInfo);
    const { j: startColumn, i: startRow } = start;
    const { j: endColumn } = end;
    // This condition will hide the column indicator when the user has selected one or more columns.
    if (startColumn === endColumn) {
      const columnLetter = getColumnLetter(startColumn);
      const cellKey = cellInfo.key ?? `${columnLetter}${startRow}`;
      const cellRef = getCellRef(tableRef, cellKey);
      const hasColSpan = hasCellColSpan(cells, cellKey, cellRef);
      if (hasColSpan && (!allowMergedSelectionIndicatorSpan || hasConfiguredColumnActions)) {
        setIndicatorPosition(null);
        setIsIndicatorActionable(false);
        setShowIndicator(false);
        return;
      }
      if (cellRef) {
        // Check if this cell has the projection-to-ltm divider
        const hasProjectionToLtmDivider = cellRef.classList.contains('projection-to-ltm-divider');

        // Check if the previous cell has the projection-to-ltm divider
        const previousCell = cellRef.previousElementSibling as HTMLElement;
        const previousHasProjectionToLtmDivider = previousCell?.classList.contains('projection-to-ltm-divider');

        // Check if the next cell (LTM) has the projection-to-ltm divider
        const nextCell = cellRef.nextElementSibling as HTMLElement;
        const nextHasProjectionToLtmDivider = nextCell?.classList.contains('projection-to-ltm-divider');

        let adjustedWidth = cellRef.offsetWidth;
        let adjustedLeft = cellRef.offsetLeft;

        if (hasColSpan) {
          // Read-only merged cells can still show the visual range. Editable
          // sheets with column actions are hidden above to avoid ambiguous actions.
          adjustedWidth = cellRef.offsetWidth;
          adjustedLeft = cellRef.offsetLeft;
        } else if (hasProjectionToLtmDivider) {
          // This cell has the divider, shift its position right by 12.5px and reduce width by 12.5px on the left side
          adjustedLeft = cellRef.offsetLeft + 12.5;
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else if (previousHasProjectionToLtmDivider) {
          // The previous cell has the divider, reduce this cell's width by 12.5px on the left side
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else if (nextHasProjectionToLtmDivider) {
          // The next cell (LTM) has the divider, reduce this cell's width by 12.5px on the right side
          adjustedWidth = cellRef.offsetWidth - 12.5;
        } else {
          // For regular columns (including new projection columns), use the exact cell dimensions
          adjustedWidth = cellRef.offsetWidth;
          adjustedLeft = cellRef.offsetLeft;
        }

        setIndicatorPosition({
          left: adjustedLeft,
          width: adjustedWidth,
        });
        setIsIndicatorActionable(!hasColSpan);
        setShowIndicator(true);
      } else {
        setIndicatorPosition(null);
        setIsIndicatorActionable(false);
        setShowIndicator(false);
      }
    } else {
      setIndicatorPosition(null);
      setIsIndicatorActionable(false);
      setShowIndicator(false);
    }
  }, [
    selectedCell,
    displayColumnIndicator,
    cells,
    tableRef,
    hiddenColumns,
    allowMergedSelectionIndicatorSpan,
    hasConfiguredColumnActions,
  ]);

  const handleOnMoreOptionsClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  // This uses a regular expression to replace all digits with an empty string
  const getLetterFromKey = (key?: string): string => key?.replace(/\d/g, '') ?? '';

  const onCloseDropdown = (): void => setAnchorEl(null);

  const handleOnCopyClick = useCallback(() => {
    onCloseDropdown();
    if (!cell) {
      return;
    }
    const { columnLegend, key } = cell;
    const columnLetter = columnLegend ?? getLetterFromKey(key);
    if (columnLetter) {
      const valuesToCopy = getValuesToCopy(
        cells,
        columnLetter,
        true,
        format?.currency?.exchangeRate,
        format?.currency?.units
      );
      copyToClipboard(valuesToCopy, infoNotification, errorNotification);
    }
  }, [cell, cells, infoNotification, errorNotification, format]);

  const removeIndicator = useCallback(() => {
    setClearSelected(true);
    setSelectedCell(undefined);
    setIndicatorPosition(null);
    setIsIndicatorActionable(false);
    setShowIndicator(false);
  }, [setClearSelected, setSelectedCell]);

  const handleToggleFrozenPane = useCallback(() => {
    setAnchorEl(null);
    if (cell?.key && onToggleFrozenPaneAtCell) {
      const frozenPaneColumnLegend = frozenPaneCellKey?.replace(/\d+$/, '');
      const isCurrentBoundary = Boolean(cell.columnLegend && frozenPaneColumnLegend === cell.columnLegend);
      onToggleFrozenPaneAtCell(isCurrentBoundary && frozenPaneCellKey ? frozenPaneCellKey : String(cell.key));
      removeIndicator();
    }
  }, [cell?.columnLegend, cell?.key, frozenPaneCellKey, onToggleFrozenPaneAtCell, removeIndicator]);

  const columnIndex = useMemo(() => {
    if (!cell) {
      return null;
    }
    const { y: columnPos, columnLegend, key } = cell;
    const cellColumnLegend = columnLegend ?? getLetterFromKey(key);
    const index = columnPos ?? getColumnIndex(cellColumnLegend);
    return index;
  }, [cell]);

  const hasAISuggestions = useMemo(() => {
    if (!cell || !cells) {
      return false;
    }
    const { columnLegend, key } = cell;
    const colLetter = columnLegend ?? getLetterFromKey(key);
    if (!colLetter) {
      return false;
    }
    return Object.entries(cells).some(([cellKey, c]) => {
      if (!cellKey.startsWith(colLetter)) return false;
      // PENDING findings only: a settled (accepted OR dismissed) column has
      // nothing left to bulk-accept/dismiss, so the AI menu entries hide.
      // is_accepted alone is wrong here - dismissed keeps is_accepted=false.
      return isPendingMeta((c as any).aiFieldMetadata);
    });
  }, [cell, cells]);

  const handleAcceptAllSuggestions = useCallback(() => {
    onCloseDropdown();
    if (cell) {
      const { columnId } = cell;
      const currentColumnId = getColumnId(columnId, columnIndex, columns);
      if (currentColumnId != null) {
        acceptAllFields(Number(currentColumnId));
      }
    }
  }, [cell, columnIndex, columns, acceptAllFields]);

  const handleDismissAllSuggestions = useCallback(() => {
    onCloseDropdown();
    if (cell) {
      const { columnId } = cell;
      const currentColumnId = getColumnId(columnId, columnIndex, columns);
      if (currentColumnId != null) {
        dismissAllFields(Number(currentColumnId));
      }
    }
  }, [cell, columnIndex, columns, dismissAllFields]);

  const showDeleteOption = useMemo(() => {
    if (
      (allowDeleteColumn || allowDeleteOnlySpecificColumn) &&
      columns &&
      !isNil(columnIndex) &&
      columnIndex < columns.length
    ) {
      const sortedColumns = sortBy(columns, ['order']);
      const column = sortedColumns[columnIndex] as Column;
      const isLTMOrNTMColumnLegend = column?.isLTM || column?.isNTM;
      const isDeletable = !isLTMOrNTMColumnLegend && column && !('parentColumn' in column);
      const allowDelete = (allowDeleteColumn || allowConfirmAndDeleteColumn) && isDeletable;
      const allowDeleteCondition = allowDeleteOnlySpecificColumn
        ? column.isDeleteableColumn && allowDelete
        : allowDelete;
      return allowDeleteCondition;
    }
    return false;
  }, [columnIndex, allowConfirmAndDeleteColumn, allowDeleteOnlySpecificColumn, allowDeleteColumn, columns]);

  const hideIndicator = useCallback(() => {
    setSelectedCell(null);
    setIndicatorPosition(null);
    setShowIndicator(false);
  }, [setSelectedCell]);

  const getColumnId = (
    cellColumnId: number | string | undefined,
    colIndex: number | null,
    cols: ReverseParsedFundOwnershipDetail[]
  ): number | string | null => {
    if (cellColumnId) {
      return cellColumnId;
    }
    if (cols && colIndex !== null) {
      return cols[colIndex]?.id ?? colIndex;
    }
    return colIndex;
  };

  const handleOnDeleteClick = useCallback(() => {
    onCloseDropdown();
    if (cell) {
      const { columnId } = cell;
      const currentColumnId = getColumnId(columnId, columnIndex, columns);
      if (handleDeleteColumn && showDeleteOption) {
        // If handleDeleteColumn expects 3 arguments based on its signature, it comes from ScalarSpreadsheet
        // If so, we will pass columnIndex, columnId, wb
        if (handleDeleteColumn.length === 4 && !isNil(currentColumnId) && workbook) {
          handleDeleteColumn(columnIndex, currentColumnId, workbook, hideIndicator);
        }
        // If it expects 1 argument, it comes from FeaturedSpreadsheet
        // If so, we will pass columnIndex
        if (handleDeleteColumn.length === 2 && columnIndex !== null) {
          handleDeleteColumn(columnIndex, hideIndicator);
        }
      }
    }
  }, [cell, handleDeleteColumn, workbook, columnIndex, showDeleteOption, columns, hideIndicator]);

  const handleOnCloneClick = useCallback(() => {
    onCloseDropdown();
    if (cell && cloneColumn && columnIndex !== null) {
      if (!isNil(columnIndex)) {
        cloneColumn(columnIndex);
      }
    }
  }, [cell, cloneColumn, columnIndex]);

  const handleOnAddMultipleClick = (): void => {
    onCloseDropdown();
    if (addMultipleColumns) {
      addMultipleColumns(multipleValue);
    }
  };

  const currentColumn = columns && columnIndex !== null ? (columns[columnIndex] as Column) : null;
  const field = currentColumn?.fields?.[0];
  const columnKey = field ? Object.values(field)?.[0] : null;
  const fieldIsString = !!currentColumn?.isStringColumn;
  const fieldIsDate = !!currentColumn?.isDateColumn;

  const isSortableColumn = useMemo(
    () => !!allowSortColumn && !!currentColumn?.isSortableColumn,
    [allowSortColumn, currentColumn]
  );

  const SortDescription = useMemo(() => {
    if (isEmpty(sortedColumn)) {
      return SortDescIcon;
    }

    if (sortedColumn && columnKey && columnKey in sortedColumn) {
      return getStringValue(sortedColumn?.[columnKey]) === SORT_DESC ? SortAscIcon : SortDescIcon;
    }

    return SortDescIcon;
  }, [columnKey, sortedColumn]);

  const handleSortColumnClick = (): void => {
    onCloseDropdown();

    if (isSortableColumn && columnKey && sortColumnFn) {
      sortColumnFn(columnKey, fieldIsString, fieldIsDate);
    }
  };

  // With this function we will determine if do we have to show or not the dropdown menu on the column indicator
  const showMenuOptions = useMemo(
    () =>
      Boolean(onToggleFrozenPaneAtCell) ||
      allowCopyColumn ||
      allowReorderColumns ||
      showDeleteOption ||
      isSortableColumn ||
      hasAISuggestions,
    [
      allowCopyColumn,
      allowReorderColumns,
      isSortableColumn,
      showDeleteOption,
      hasAISuggestions,
      onToggleFrozenPaneAtCell,
    ]
  );
  const shouldShowColumnActions = showIndicator && isIndicatorActionable;
  const frozenPaneColumnLegend = frozenPaneCellKey?.replace(/\d+$/, '');
  const isCurrentFrozenPaneBoundary = Boolean(cell?.columnLegend && frozenPaneColumnLegend === cell.columnLegend);
  const freezeColumnLabel = isCurrentFrozenPaneBoundary ? 'Unfreeze this column' : 'Freeze this column';

  return (
    <StyledColumnIndicator
      id={`column-indicator-${(cell?.columnLegend ?? '') + 0}`}
      className={allowReorderColumns ? 'column-rearrange' : ''}
      allowReorderColumns={allowReorderColumns && isIndicatorActionable}
      style={{
        display: showIndicator && indicatorPosition ? undefined : 'none',
        left: indicatorPosition?.left,
        width: indicatorPosition?.width,
      }}>
      {shouldShowColumnActions && allowReorderColumns && (
        <StyledMenuButton cursorType="move">
          <StyledMoreHorizIcon fontSize="small" />
        </StyledMenuButton>
      )}
      {shouldShowColumnActions && showMenuOptions && (
        <>
          <StyledMenuButton
            cursorType="pointer"
            emphasizeViewerFreezeAction={Boolean(onToggleFrozenPaneAtCell)}
            onClick={handleOnMoreOptionsClick}>
            <StyledKeyboardArrowDownIcon fontSize="small" />
          </StyledMenuButton>
          <Popper open={open} anchorEl={anchorEl} id={open ? 'transitions-popper' : undefined}>
            <StyledMenu
              compactViewerOptions={Boolean(onToggleFrozenPaneAtCell)}
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
              }}
              {...(!onToggleFrozenPaneAtCell
                ? {
                  PaperProps: {
                    style: {
                      minWidth: indicatorPosition?.width,
                    },
                  },
                }
                : {})}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={onCloseDropdown}>
              {onToggleFrozenPaneAtCell && (
                <MenuItem data-testid="toggle-frozen-columns" onClick={handleToggleFrozenPane} disableRipple>
                  <PushPinOutlined />
                  {freezeColumnLabel}
                  <Checkbox
                    aria-label={freezeColumnLabel}
                    checked={isCurrentFrozenPaneBoundary}
                    onChange={handleToggleFrozenPane}
                    onClick={event => event.stopPropagation()}
                    size="small"
                  />
                </MenuItem>
              )}
              {allowCopyColumn && (
                <MenuItem onClick={handleOnCopyClick} disableRipple>
                  COPY
                </MenuItem>
              )}
              {allowCloneColumn && (
                <MenuItem onClick={handleOnCloneClick} disableRipple>
                  CLONE
                </MenuItem>
              )}
              {allowAddMultipleColumns && addMultipleColumns && (
                <AddMultipleMenuItem
                  onAddMultiple={handleOnAddMultipleClick}
                  multipleValue={multipleValue}
                  setMultipleValue={setMultipleValue}
                />
              )}
              {hasAISuggestions && (
                <StyledAcceptAllMenuItem onClick={handleAcceptAllSuggestions} disableRipple>
                  <Typography sx={{ ...(aiGradientTextSx as object), fontWeight: 600, fontSize: '0.875rem' }}>
                    ACCEPT ALL SUGGESTIONS
                  </Typography>
                </StyledAcceptAllMenuItem>
              )}
              {hasAISuggestions && (
                <StyledAcceptAllMenuItem onClick={handleDismissAllSuggestions} disableRipple>
                  <Typography sx={{ ...(aiGradientTextSx as object), fontWeight: 600, fontSize: '0.875rem' }}>
                    DISMISS ALL SUGGESTIONS
                  </Typography>
                </StyledAcceptAllMenuItem>
              )}
              {showDeleteOption && <StyledDeleteMenuItem onClick={handleOnDeleteClick}>DELETE</StyledDeleteMenuItem>}

              {isSortableColumn && (
                <MenuItem onClick={handleSortColumnClick}>
                  <SortDescription />
                </MenuItem>
              )}
            </StyledMenu>
          </Popper>
        </>
      )}
    </StyledColumnIndicator>
  );
};

export default ColumnIndicator;
